import { useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import i18n from "@/shared/i18n";
import { isApiError } from "@/shared/api/client";
import { AuthProvider } from "@/features/auth/auth.context";
import { useCountryLanguage } from "@/features/panel/useCountryLanguage";

/**
 * Un `QueryClient` por montaje (no un singleton de módulo): así cada test
 * arranca con caché limpia y no hay estado compartido entre pruebas.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /*
         * El API vive en el MISMO origen: el detector de "offline" de
         * react-query no protege de nada y en producción llegó a
         * quedarse ATORADO en `paused` (evento offline fantasma al
         * arrancar), dejando el catálogo en "Cargando…" para siempre.
         * `always` lo apaga: si la red falla, el error se ENSEÑA.
         */
        networkMode: "always",
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Un error de contrato o de permisos no mejora reintentando.
          if (isApiError(error)) {
            if (error.code === "UNAUTHENTICATED" || error.code === "FORBIDDEN") return false;
            if (error.code === "VALIDATION_ERROR" || error.code === "NOT_FOUND") return false;
          }
          return failureCount < 2;
        },
      },
    },
  });
}

/**
 * Un componente sin marcas, solo para colgar el efecto donde ya hay
 * sesión. Un hook no se puede llamar en `Providers`: ahí todavía no
 * existe el `AuthProvider` que lee.
 */
function IdiomaDelPais() {
  useCountryLanguage();
  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {/*
          AuthProvider va DENTRO de QueryClientProvider y por encima del
          router: intenta restaurar la sesión una sola vez al arrancar y
          no pinta nada de la app hasta saber el resultado. Así nadie ve
          el login medio segundo para que se lo cambien por el panel.
        */}
        <AuthProvider>
          {/*
            El idioma sigue al PAÍS de la cuenta (Anahí, 1-sep). Va DENTRO
            de `AuthProvider` —necesita la sesión— y por encima de todo lo
            que pinta, para que ninguna pantalla llegue a verse en un
            idioma que no le corresponde. Ver `useCountryLanguage`.
          */}
          <IdiomaDelPais />
          {children}
        </AuthProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
